import React, { useEffect, useState, useCallback } from "react";
import { Store, Package, ShoppingCart, Wallet, Plus, Truck, Landmark, CheckCircle2, Clock, MapPin, Printer, FileText } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Papa from "papaparse";
import { rp, fmtDate, ORDER_STATUS, DISPUTE_STATUS, CONDITIONS } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { Layout } from "@/components/Layout";
import { ConditionBadge } from "@/components/ConditionBadge";
import { FileUpload } from "@/components/FileUpload";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const emptyProduct = { title: "", brand: "", category: "Fashion", price: "", original_price: "", condition: "good",
  description: "", photos: [], defect_photos: [], defect_notes: "", size: "", color: "", purchase_year: "", authenticity: "", negotiable: false, shipping_info: "", cod_enabled: false };

export default function SellerDashboard() {
  const { lang } = useApp();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ents, setEnts] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [banks, setBanks] = useState([]);
  const [cats, setCats] = useState([]);
  const [pForm, setPForm] = useState(emptyProduct);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [ship, setShip] = useState({ courier: "", tracking_number: "" });
  const [bank, setBank] = useState({ bank_name: "", account_number: "", account_holder: "", bank_code: "", account_type: "1" });
  const [bankOptions, setBankOptions] = useState([]);
  const [selBank, setSelBank] = useState("");

  const loadAll = useCallback(() => {
    api.get("/seller/me").then(({ data }) => setData(data)).catch(() => {});
    api.get("/seller/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/seller/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/seller/entitlements").then(({ data }) => setEnts(data)).catch(() => {});
    api.get("/seller/settlements").then(({ data }) => setSettlements(data)).catch(() => {});
    api.get("/seller/bank-accounts").then(({ data }) => setBanks(data)).catch(() => {});
    api.get("/tarapay/bank-options").then(({ data }) => setBankOptions(data.list || [])).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const openAdd = () => { setEditId(null); setPForm(emptyProduct); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditId(p.id);
    setPForm({ title: p.title || "", brand: p.brand || "", category: p.category || "Fashion", price: p.price || "",
      original_price: p.original_price || "", condition: p.condition || "good", description: p.description || "",
      photos: p.photos || [], defect_photos: p.defect_photos || [], defect_notes: p.defect_notes || "",
      size: p.size || "", color: p.color || "", purchase_year: p.purchase_year || "", authenticity: p.authenticity || "",
      negotiable: !!p.negotiable, shipping_info: p.shipping_info || "", cod_enabled: !!p.cod_enabled });
    setDialogOpen(true);
  };
  const submitProduct = async () => {
    try {
      const payload = { ...pForm, price: Number(pForm.price), original_price: pForm.original_price ? Number(pForm.original_price) : null,
        photos: pForm.photos, defect_photos: pForm.defect_photos };
      if (editId) { await api.put(`/seller/products/${editId}`, payload); toast.success("Produk diperbarui. Menunggu moderasi ulang."); }
      else { await api.post("/seller/products", payload); toast.success("Produk dibuat. Menunggu moderasi admin."); }
      setPForm(emptyProduct); setEditId(null); setDialogOpen(false); loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const shipOrder = async (id) => {
    try { await api.post(`/seller/orders/${id}/ship`, ship); toast.success("Pesanan dikirim"); setShip({ courier: "", tracking_number: "" }); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addBank = async () => {
    try { await api.post("/seller/bank-accounts", bank); toast.success("Rekening ditambahkan (perlu verifikasi admin)"); setBank({ bank_name: "", account_number: "", account_holder: "" }); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const requestSettlement = async () => {
    const eligible = ents.filter((e) => e.status === "eligible").map((e) => e.id);
    if (!selBank) return toast.error("Pilih rekening tujuan");
    if (eligible.length === 0) return toast.error("Tidak ada entitlement yang layak");
    try { await api.post("/seller/settlements", { bank_account_id: selBank, entitlement_ids: eligible });
      toast.success("Permintaan settlement dikirim"); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const downloadSettlementInvoice = async (st) => {
    const tid = toast.loading("Menyiapkan invoice settlement PDF...");
    try {
      const res = await api.get(`/seller/settlements/${st.id}/invoice`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Settlement-Invoice-${st.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.dismiss(tid);
      toast.success("Invoice settlement berhasil diunduh");
    } catch (e) {
      toast.dismiss(tid);
      toast.error(errMsg(e, "Gagal mengunduh invoice settlement"));
    }
  };
  const downloadOrderInvoice = async (o) => {
    const tid = toast.loading("Menyiapkan invoice PDF...");
    try {
      const res = await api.get(`/orders/${o.id}/invoice`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${o.order_number || o.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.dismiss(tid);
      toast.success("Invoice pesanan berhasil diunduh");
    } catch (e) {
      toast.dismiss(tid);
      toast.error(errMsg(e, "Gagal mengunduh invoice"));
    }
  };
  const [disputeReason, setDisputeReason] = useState("");
  const openSellerDispute = async (id) => {
    try { await api.post(`/seller/orders/${id}/dispute`, { reason: disputeReason });
      toast.success("Sengketa diajukan ke admin RELOVA"); setDisputeReason(""); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const printLabel = (o) => {
    const a = o.shipping_address || {};
    const win = window.open("", "_blank", "width=420,height=680");
    if (!win) return toast.error("Pop-up diblokir. Izinkan pop-up untuk mencetak label.");
    win.document.write(`<!doctype html><html><head><title>Label ${esc(o.order_number)}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:16px}
      .label{border:2px solid #111;padding:16px;width:340px}
      .brand{font-size:18px;font-weight:800;letter-spacing:1px}
      .muted{color:#555;font-size:11px}
      .row{margin-top:8px;font-size:13px}
      .to{font-size:16px;font-weight:800;margin-top:6px}
      .addr{font-size:14px;line-height:1.55;margin-top:4px}
      .cod{margin-top:12px;border:2px dashed #111;padding:10px;font-weight:800;font-size:17px;text-align:center}
      hr{border:none;border-top:1.5px dashed #111;margin:10px 0}
      @media print{body{padding:0}}
    </style></head><body><div class="label">
      <div class="brand">RELOVA | PRELOVED</div>
      <div class="muted">Label Pengiriman · ${esc(o.order_number)}</div><hr/>
      <div class="row"><b>Dari:</b> ${esc(store?.name || "-")} — ${esc(store?.location || "Indonesia")}</div><hr/>
      <div class="to">Kepada: ${esc(a.recipient || "-")}</div>
      <div class="addr">${esc(a.phone || "")}<br/>${esc(a.address || "")}<br/>${esc(a.city || "")} ${esc(a.postal_code || "")}</div>
      ${o.courier ? `<div class="row"><b>Kurir:</b> ${esc(o.courier)} · Resi: ${esc(o.tracking_number || "-")}</div>` : ""}
      ${o.payment_mode === "cod"
        ? `<div class="cod">COD — TAGIH ${rp(o.amount)}</div>`
        : `<div class="row muted">LUNAS via QRIS — jangan tagih pembeli</div>`}
    </div><script>window.print()</script></body></html>`);
    win.document.close();
  };
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const parseCsv = (file) => {
    if (!file) return;
    Papa.parse(file, { header: true, skipEmptyLines: true, complete: (res) => {
      setImportResult(null); setImportRows(res.data || []); } });
  };
  const runImport = async () => {
    if (!importRows || importRows.length === 0) return toast.error("Pilih file CSV terlebih dahulu");
    setImporting(true);
    try {
      const { data } = await api.post("/seller/products/bulk", { rows: importRows });
      setImportResult(data);
      toast.success(`${data.created} produk diimpor (menunggu moderasi admin)`);
      loadAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setImporting(false); }
  };

  const s = data?.stats || {};
  const store = data?.store;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-rl-secondary text-white grid place-items-center font-serif text-xl font-bold">{store?.name?.[0]}</div>
          <div>
            <h1 className="font-serif text-2xl font-bold text-rl-ink">{store?.name || "Dashboard Toko"}</h1>
            <a href={store ? `/toko/${store.slug}` : "#"} className="text-sm text-rl-primary hover:underline">/toko/{store?.slug}</a>
          </div>
          <span className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${data?.seller?.kyc_status === "verified" ? "bg-emerald-100 text-emerald-800" : data?.seller?.kyc_status === "rejected" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
            KYC: {data?.seller?.kyc_status}</span>
        </div>

        {store?.balance_frozen && (
          <div data-testid="banner-balance-frozen" className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            <b>Saldo dibekukan oleh admin.</b> {store.balance_frozen_reason ? `Alasan: ${store.balance_frozen_reason}.` : ""} Anda tidak dapat mengajukan pencairan dana baru sampai saldo diaktifkan kembali.
          </div>
        )}
        {data?.seller?.kyc_status === "rejected" && (
          <div data-testid="banner-kyc-rejected" className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            <b>KYC ditolak.</b> {data.seller.kyc_note ? `Catatan admin: ${data.seller.kyc_note}.` : ""} Produk Anda tidak tayang ke publik sampai dokumen diverifikasi ulang.
          </div>
        )}
        {data?.seller?.kyc_status === "pending" && (
          <div data-testid="banner-kyc-pending" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <b>KYC sedang ditinjau admin.</b> Produk Anda belum tayang ke publik sampai KYC disetujui.
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Package} label="Listing Aktif" value={s.active_listings || 0} />
          <StatCard icon={ShoppingCart} label="Total Pesanan" value={s.total_orders || 0} />
          <StatCard icon={Wallet} label="Dana Layak Cair" value={rp(s.eligible_amount || 0)} />
          <StatCard icon={Store} label="Penjualan Selesai" value={store?.completed_sales || 0} />
        </div>

        <Tabs defaultValue="products" className="mt-8">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="products" data-testid="s-tab-products">Produk</TabsTrigger>
            <TabsTrigger value="orders" data-testid="s-tab-orders">Pesanan</TabsTrigger>
            <TabsTrigger value="entitlements" data-testid="s-tab-entitlements">Hasil Penjualan</TabsTrigger>
            <TabsTrigger value="settlements" data-testid="s-tab-settlements">Settlement</TabsTrigger>
            <TabsTrigger value="payment" data-testid="s-tab-payment">Payment Profile</TabsTrigger>
          </TabsList>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-rl-ink">Produk Saya ({products.length})</h3>
              <div className="flex gap-2">
                <button data-testid="import-csv-btn" onClick={() => { setImportRows(null); setImportResult(null); setImportOpen(true); }} className="inline-flex items-center gap-1.5 rounded-full border border-rl-border hover:border-rl-primary text-rl-ink text-sm font-semibold px-4 py-2">Impor CSV</button>
                <button data-testid="add-product-btn" onClick={openAdd} className="inline-flex items-center gap-1.5 rounded-full bg-rl-primary text-white text-sm font-semibold px-4 py-2"><Plus className="w-4 h-4" />Tambah Produk</button>
              </div>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editId ? "Edit Listing" : "Listing Preloved Baru"}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Inp t="prod-title" label="Judul" v={pForm.title} on={(v) => setPForm({ ...pForm, title: v })} />
                    <div className="grid grid-cols-2 gap-3">
                      <Inp t="prod-brand" label="Merek" v={pForm.brand} on={(v) => setPForm({ ...pForm, brand: v })} />
                      <div><label className="text-xs font-semibold text-rl-muted">Kategori</label>
                        <select data-testid="prod-category" value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1">
                          {cats.map((c) => <option key={c.id} value={c.name_en}>{lang === "id" ? c.name : c.name_en}</option>)}</select></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Inp t="prod-price" label="Harga" type="number" v={pForm.price} on={(v) => setPForm({ ...pForm, price: v })} />
                      <Inp t="prod-orig" label="Harga Asli (opsional)" type="number" v={pForm.original_price} on={(v) => setPForm({ ...pForm, original_price: v })} />
                    </div>
                    <div><label className="text-xs font-semibold text-rl-muted">Kondisi</label>
                      <select data-testid="prod-condition" value={pForm.condition} onChange={(e) => setPForm({ ...pForm, condition: e.target.value })} className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1">
                        {Object.entries(CONDITIONS).map(([k, v]) => <option key={k} value={k}>{v[lang]}</option>)}</select></div>
                    <div className="grid grid-cols-2 gap-3">
                      <Inp t="prod-size" label="Ukuran" v={pForm.size} on={(v) => setPForm({ ...pForm, size: v })} />
                      <Inp t="prod-color" label="Warna" v={pForm.color} on={(v) => setPForm({ ...pForm, color: v })} />
                    </div>
                    <FileUpload testid="prod-photos" label="Foto Produk (unggah dari perangkat)" value={pForm.photos} onChange={(v) => setPForm({ ...pForm, photos: v })} max={6} />
                    <FileUpload testid="prod-defect-photos" label="Foto Bagian Minus (jujur & wajib bila ada)" value={pForm.defect_photos} onChange={(v) => setPForm({ ...pForm, defect_photos: v })} max={4} />
                    <div><label className="text-xs font-semibold text-rl-muted">Deskripsi</label>
                      <textarea data-testid="prod-desc" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1 min-h-[70px]" /></div>
                    <div><label className="text-xs font-semibold text-rl-muted">Catatan Kekurangan (wajib jujur)</label>
                      <textarea data-testid="prod-defect-notes" value={pForm.defect_notes} onChange={(e) => setPForm({ ...pForm, defect_notes: e.target.value })} className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1" /></div>
                    <Inp t="prod-auth" label="Info Keaslian" v={pForm.authenticity} on={(v) => setPForm({ ...pForm, authenticity: v })} />
                    <label className="flex items-center gap-2 text-sm"><input data-testid="prod-nego" type="checkbox" checked={pForm.negotiable} onChange={(e) => setPForm({ ...pForm, negotiable: e.target.checked })} />Bisa nego</label>
                    <label className="flex items-center gap-2 text-sm"><input data-testid="prod-cod" type="checkbox" checked={pForm.cod_enabled} onChange={(e) => setPForm({ ...pForm, cod_enabled: e.target.checked })} />Aktifkan COD (bayar di tempat, langsung ke Anda)</label>
                  </div>
                  <DialogFooter><button data-testid="submit-product" onClick={submitProduct} disabled={!pForm.title || !pForm.price} className="rounded-full bg-rl-primary text-white font-bold px-6 py-2.5 disabled:opacity-50">Simpan</button></DialogFooter>
                </DialogContent>
              </Dialog>
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Impor Produk Massal (CSV)</DialogTitle></DialogHeader>
                <ol className="text-sm text-rl-ink2 list-decimal list-inside space-y-1.5">
                  <li>Unduh template: <a data-testid="download-template" href="/templates/template_produk_relova.csv" download className="text-rl-primary font-semibold hover:underline">template_produk_relova.csv</a></li>
                  <li>Isi di Excel/Sheets (bisa buka → isi → simpan sebagai CSV). Kolom wajib: <b>title, category, price, condition</b>.</li>
                  <li>condition: <span className="font-mono text-xs">like_new / excellent / good / fair</span> · ya/tidak untuk negotiable & cod_enabled · pisahkan beberapa foto dengan <span className="font-mono text-xs">|</span> (foto berupa URL, atau kosongkan lalu unggah via Edit).</li>
                  <li>Kategori harus sama dengan daftar kategori RELOVA (lihat halaman Beranda).</li>
                </ol>
                <input type="file" accept=".csv,text/csv" data-testid="import-file" onChange={(e) => parseCsv(e.target.files?.[0])}
                  className="w-full rounded-lg border border-dashed border-rl-border p-4 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-rl-primary file:text-white file:font-semibold file:px-4 file:py-1.5" />
                {importRows && !importResult && (
                  <p data-testid="import-preview" className="text-sm text-rl-muted">Terbaca <b className="text-rl-ink">{importRows.length}</b> baris produk. Klik "Impor Sekarang" untuk mengirim.</p>
                )}
                {importResult && (
                  <div data-testid="import-result" className="rounded-xl border border-rl-border bg-rl-bg2 p-3 text-sm space-y-1.5">
                    <p><b className="text-emerald-700">{importResult.created}</b> produk berhasil diimpor (menunggu moderasi).</p>
                    {(importResult.errors || []).map((err, i) => (
                      <p key={i} data-testid={`import-error-${i}`} className="text-rose-600 text-xs">Baris {err.row}: {err.error}</p>
                    ))}
                  </div>
                )}
                <DialogFooter>
                  <button data-testid="import-cancel" onClick={() => setImportOpen(false)} className="rounded-full border border-rl-border px-5 py-2 text-sm font-semibold">Tutup</button>
                  <button data-testid="import-submit" onClick={runImport} disabled={importing || !importRows || !!importResult} className="rounded-full bg-rl-primary text-white font-bold px-6 py-2.5 text-sm disabled:opacity-50">
                    {importing ? "Mengimpor..." : "Impor Sekarang"}</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.id} data-testid={`s-product-${p.id}`} className="rounded-xl border border-rl-border bg-white p-3 flex gap-3">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-rl-bg2 shrink-0">{(p.photos || [])[0] && <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-rl-ink truncate">{p.title}</div>
                    <div className="font-bold text-rl-ink text-sm">{rp(p.price)}</div>
                    <div className="mt-1 flex gap-1 flex-wrap">
                      <ConditionBadge condition={p.condition} />
                      <ModBadge status={p.moderation_status} />
                      {p.status === "sold" && <span className="text-[10px] font-semibold rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">Terjual</span>}
                    </div>
                    {p.status !== "sold" && <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="mt-2 text-xs font-semibold text-rl-primary hover:underline">Edit</button>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6">
            <div className="space-y-3">
              {orders.length === 0 && <p className="text-rl-muted text-center py-8">Belum ada pesanan.</p>}
              {orders.map((o) => {
                const st = ORDER_STATUS[o.status] || ORDER_STATUS.created;
                return (
                  <div key={o.id} data-testid={`s-order-${o.id}`} className="rounded-xl border border-rl-border bg-white p-4 flex flex-wrap items-center gap-4">
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-rl-bg2 shrink-0">{o.product_snapshot?.photo && <img src={o.product_snapshot.photo} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1 min-w-[140px]"><div className="font-mono text-xs text-rl-muted">{o.order_number}</div>
                      <div className="font-semibold text-sm">{o.product_snapshot?.title}</div>
                      <div className="text-sm">{rp(o.amount)} · <span className="text-rl-secondary">Net {rp(o.seller_net)}</span></div>
                      {o.shipping_address && (
                        <div data-testid={`ship-address-${o.id}`} className="mt-1.5 flex items-start gap-1 text-xs text-rl-muted">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                          <span><b className="text-rl-ink2">{o.shipping_address.recipient}</b> ({o.shipping_address.phone})<br />
                            {o.shipping_address.address}, {o.shipping_address.city} {o.shipping_address.postal_code}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {o.payment_mode === "cod" && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-teal-100 text-teal-800">COD</span>}
                      <span className={`rounded-full px-3 py-0.5 text-xs font-semibold ${st.cls}`}>{st[lang]}</span>
                      {o.dispute_status && o.dispute_status !== "none" && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${DISPUTE_STATUS[o.dispute_status]?.cls}`}>{DISPUTE_STATUS[o.dispute_status]?.[lang]}</span>}
                    </div>
                    {o.shipping_address && ["paid", "cod_confirmed", "shipped", "completed"].includes(o.status) && (
                      <button data-testid={`label-btn-${o.id}`} onClick={() => printLabel(o)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-rl-border hover:border-rl-primary text-rl-ink text-sm font-semibold px-4 py-2 transition-colors">
                        <Printer className="w-4 h-4" />Label</button>
                    )}
                    <button data-testid={`order-invoice-btn-${o.id}`} onClick={() => downloadOrderInvoice(o)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-rl-border hover:border-rl-primary text-rl-ink text-sm font-semibold px-4 py-2 transition-colors"
                      title="Unduh Invoice PDF">
                      <FileText className="w-4 h-4 text-rl-primary" />Invoice</button>
                    {["paid", "cod_confirmed"].includes(o.status) && (
                      <Dialog>
                        <DialogTrigger asChild><button data-testid={`ship-btn-${o.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-rl-secondary text-white text-sm font-semibold px-4 py-2"><Truck className="w-4 h-4" />Kirim</button></DialogTrigger>
                        <DialogContent><DialogHeader><DialogTitle>Kirim Pesanan — {o.order_number}</DialogTitle></DialogHeader>
                          {o.shipping_address && (
                            <div className="rounded-lg bg-rl-bg2 p-3 text-xs text-rl-ink2">
                              <b>Kirim ke:</b> {o.shipping_address.recipient} ({o.shipping_address.phone})<br />
                              {o.shipping_address.address}, {o.shipping_address.city} {o.shipping_address.postal_code}
                            </div>
                          )}
                          <Inp t="ship-courier" label="Kurir" v={ship.courier} on={(v) => setShip({ ...ship, courier: v })} />
                          <Inp t="ship-tracking" label="No. Resi" v={ship.tracking_number} on={(v) => setShip({ ...ship, tracking_number: v })} />
                          <DialogFooter><button data-testid={`submit-ship-${o.id}`} onClick={() => shipOrder(o.id)} disabled={!ship.courier || !ship.tracking_number} className="rounded-full bg-rl-primary text-white font-bold px-6 py-2.5 disabled:opacity-50">Konfirmasi Kirim</button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                    {o.payment_mode === "cod" && (!o.dispute_status || o.dispute_status === "none") && !["cancelled", "refunded"].includes(o.status) && (
                      <Dialog>
                        <DialogTrigger asChild><button data-testid={`seller-dispute-btn-${o.id}`} className="rounded-full border border-rose-300 text-rose-600 text-sm font-semibold px-4 py-2 hover:bg-rose-50">Ajukan Sengketa</button></DialogTrigger>
                        <DialogContent><DialogHeader><DialogTitle>Ajukan Sengketa (COD) — {o.order_number}</DialogTitle></DialogHeader>
                          <p className="text-xs text-rl-muted">Jelaskan masalah (mis. buyer tidak bayar / barang dikembalikan). Admin RELOVA akan menengahi.</p>
                          <textarea data-testid="seller-dispute-reason" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} className="w-full rounded-lg border border-rl-border p-3 text-sm min-h-[90px] outline-none focus:border-rl-primary" placeholder="Alasan sengketa..." />
                          <DialogFooter><button data-testid={`submit-seller-dispute-${o.id}`} onClick={() => openSellerDispute(o.id)} disabled={!disputeReason.trim()} className="rounded-full bg-rose-600 text-white font-bold px-6 py-2.5 disabled:opacity-50">Kirim Sengketa</button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* ENTITLEMENTS */}
          <TabsContent value="entitlements" className="mt-6">
            <p className="text-sm text-rl-muted mb-3">Hasil penjualan dari pesanan yang telah selesai (bukan saldo dompet umum).</p>
            <div className="rounded-xl border border-rl-border bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-rl-bg2 text-rl-muted text-xs uppercase"><tr>
                  <th className="text-left p-3">Order</th><th className="text-left p-3">Produk</th><th className="text-right p-3">Bruto</th><th className="text-right p-3">Fee</th><th className="text-right p-3">Net</th><th className="text-left p-3">Status</th></tr></thead>
                <tbody>{ents.map((e) => (
                  <tr key={e.id} data-testid={`ent-${e.id}`} className="border-t border-rl-border">
                    <td className="p-3 font-mono text-xs">{e.order_number}</td><td className="p-3">{e.product_title}</td>
                    <td className="p-3 text-right">{rp(e.gross_amount)}</td><td className="p-3 text-right text-rl-muted">{rp(e.platform_fee)}</td>
                    <td className="p-3 text-right font-bold text-rl-secondary">{rp(e.net_amount)}</td>
                    <td className="p-3"><EntBadge status={e.status} /></td></tr>))}
                  {ents.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-rl-muted">Belum ada hasil penjualan.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* SETTLEMENTS */}
          <TabsContent value="settlements" className="mt-6 space-y-6">
            <div className="rounded-xl border border-rl-border bg-white p-5">
              <h3 className="font-bold text-rl-ink flex items-center gap-2"><Landmark className="w-4 h-4" />Rekening Bank</h3>
              <div className="mt-3 space-y-2">
                {banks.map((b) => (
                  <label key={b.id} className={`flex items-center gap-3 rounded-lg border p-3 text-sm cursor-pointer ${selBank === b.id ? "border-rl-primary bg-rl-primary/5" : "border-rl-border"}`}>
                    <input type="radio" name="bank" data-testid={`bank-radio-${b.id}`} checked={selBank === b.id} onChange={() => setSelBank(b.id)} />
                    <span className="font-semibold">{b.bank_name}</span><span>{b.account_number}</span><span className="text-rl-muted">a.n {b.account_holder}</span>
                    <span className={`ml-auto text-xs font-semibold ${b.verified ? "text-emerald-600" : "text-amber-600"}`}>{b.verified ? "Terverifikasi" : "Belum verifikasi"}</span>
                  </label>
                ))}
              </div>
              <div className="mt-3 grid sm:grid-cols-3 gap-2">
                {bankOptions.length > 0 ? (
                  <div><label className="text-xs font-semibold text-rl-muted">Bank / E-wallet (TaraPay)</label>
                    <select data-testid="bank-select" value={bank.bank_code}
                      onChange={(e) => { const o = bankOptions.find((b) => String(b.bank_id) === e.target.value);
                        setBank({ ...bank, bank_code: e.target.value, bank_name: o?.bank_name || "", account_type: o?.type === 2 ? "3" : "1" }); }}
                      className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1">
                      <option value="">Pilih bank...</option>
                      {bankOptions.map((b) => <option key={b.bank_id} value={b.bank_id}>{b.bank_name}{b.type === 2 ? " (e-wallet)" : ""}</option>)}
                    </select></div>
                ) : (
                  <Inp t="bank-name" label="Bank (nama)" v={bank.bank_name} on={(v) => setBank({ ...bank, bank_name: v })} />
                )}
                <Inp t="bank-number" label="No. Rekening" v={bank.account_number} on={(v) => setBank({ ...bank, account_number: v })} />
                <Inp t="bank-holder" label="Atas Nama" v={bank.account_holder} on={(v) => setBank({ ...bank, account_holder: v })} />
              </div>
              <button data-testid="add-bank-btn" onClick={addBank} disabled={!bank.bank_name || !bank.account_number} className="mt-3 rounded-full border border-rl-border hover:border-rl-primary text-rl-ink text-sm font-semibold px-4 py-2 disabled:opacity-50">+ Tambah Rekening</button>
            </div>
            <div className="rounded-xl border border-rl-border bg-white p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-rl-ink">Ajukan Pencairan</h3>
                <button data-testid="request-settlement-btn" onClick={requestSettlement} disabled={store?.balance_frozen} className="rounded-full bg-rl-primary text-white text-sm font-bold px-5 py-2 disabled:opacity-50">{store?.balance_frozen ? "Saldo Dibekukan" : "Cairkan Dana Layak"}</button>
              </div>
              <div className="mt-4 space-y-2">
                {settlements.map((st) => (
                  <div key={st.id} data-testid={`settlement-${st.id}`} className="flex items-center gap-3 rounded-lg border border-rl-border p-3 text-sm flex-wrap">
                    <span className="font-mono text-xs">{st.id.slice(0, 8)}</span>
                    <span className="font-bold text-rl-secondary">{rp(st.amount)}</span>
                    <span className="text-rl-muted">{st.bank_snapshot?.bank_name} · {st.bank_snapshot?.account_number}</span>
                    <span className="ml-auto text-xs font-semibold capitalize">{st.status}</span>
                    {st.reference && <span className="text-xs text-rl-muted">{st.reference}</span>}
                    <button
                      data-testid={`invoice-settlement-btn-${st.id}`}
                      onClick={() => downloadSettlementInvoice(st)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-rl-primary border border-rl-border hover:border-rl-primary hover:bg-rl-primary/5 rounded-lg px-2.5 py-1.5 transition-colors"
                      title="Unduh Bukti Settlement PDF"
                    >
                      <FileText className="w-3.5 h-3.5" /> Invoice PDF
                    </button>
                  </div>
                ))}
                {settlements.length === 0 && <p className="text-rl-muted text-center py-4">Belum ada permintaan settlement.</p>}
              </div>
            </div>
          </TabsContent>

          {/* PAYMENT PROFILE */}
          <TabsContent value="payment" className="mt-6">
            <div className="rounded-xl border border-rl-border bg-white p-5 max-w-md">
              <h3 className="font-bold text-rl-ink">Payment Profile Toko</h3>
              <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <dt className="text-rl-muted">Partner</dt><dd className="font-semibold capitalize">{data?.payment_profile?.payment_partner}</dd>
                <dt className="text-rl-muted">Merchant ID (NMID)</dt><dd className="font-semibold font-mono">{data?.payment_profile?.nmid || "— (menunggu approval)"}</dd>
                <dt className="text-rl-muted">Status</dt><dd className="font-semibold">{data?.payment_profile?.status}</dd>
              </dl>
              <p className="mt-3 text-xs text-rl-muted">Merchant identity diterbitkan sesuai persetujuan partner pembayaran. Tidak dibuat otomatis.</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-rl-border bg-white p-4">
    <Icon className="w-5 h-5 text-rl-primary" /><div className="mt-2 text-xl font-bold text-rl-ink">{value}</div>
    <div className="text-xs text-rl-muted">{label}</div></div>
);
const Inp = ({ t, label, v, on, type = "text" }) => (
  <div><label className="text-xs font-semibold text-rl-muted">{label}</label>
    <input data-testid={t} type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1 outline-none focus:border-rl-primary" /></div>
);
const ModBadge = ({ status }) => {
  const m = { pending: ["bg-amber-100 text-amber-800", "Moderasi"], approved: ["bg-emerald-100 text-emerald-800", "Tayang"], rejected: ["bg-rose-100 text-rose-800", "Ditolak"] }[status] || ["bg-gray-100 text-gray-600", status];
  return <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${m[0]}`}>{m[1]}</span>;
};
const EntBadge = ({ status }) => {
  const m = { eligible: ["bg-emerald-100 text-emerald-800", CheckCircle2], settling: ["bg-blue-100 text-blue-800", Clock], settled: ["bg-gray-100 text-gray-600", CheckCircle2] }[status] || ["bg-gray-100 text-gray-600", Clock];
  const I = m[1];
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 ${m[0]}`}><I className="w-3 h-3" />{status}</span>;
};
